import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Calendar, AlertTriangle, Trash2, CalendarClock } from 'lucide-react';
import api, { cantieri } from '../utils/api';
import { useAuth } from '../context/AuthContext';

// ── Costanti ────────────────────────────────────────────────────────────────

// FE-037 — numero massimo di eventi caricati per la vista calendario.
const EVENTI_PAGE_SIZE = 200;

const TIPO_META = {
  esecuzione_lavori: { label: 'Esecuzione lavori', color: 'var(--brand)' },
  fase_cantiere:     { label: 'Fase cantiere',      color: 'var(--zone-blue)' },
  milestone:         { label: 'Milestone',           color: 'var(--zone-amber)' },
  sopralluogo:       { label: 'Sopralluogo',        color: 'var(--zone-acqua)' },
  chiamata:          { label: 'Chiamata',            color: 'var(--brand)' },
  consegna:          { label: 'Consegna materiali',  color: 'var(--warn)' },
  preventivo:        { label: 'Invio preventivo',    color: 'var(--zone-amber)' },
  fattura:           { label: 'Emissione fattura',   color: 'var(--ds-danger)' },
  riunione:          { label: 'Riunione',            color: 'var(--ds-muted)' },
  scadenza:          { label: 'Scadenza',            color: 'var(--ds-danger)' },
  altro:             { label: 'Altro',               color: 'var(--ds-muted)' },
};
const TIPI = Object.keys(TIPO_META);
// Tipi selezionabili dall'utente nel panel creazione: esclusi fase_cantiere/milestone
// (gestiti automaticamente dai signal piano_lavori, non creabili manualmente)
const TIPI_UTENTE = TIPI.filter(t => !['fase_cantiere', 'milestone'].includes(t));

const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MESI   = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                 'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// ── Helpers ─────────────────────────────────────────────────────────────────

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function getMonthCells(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay  = new Date(year, month + 1, 0);
  const cells    = [];
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;
  for (let i = 0; i < startDow; i++)
    cells.push({ date: new Date(year, month, 1 - (startDow - i)), currentMonth: false });
  for (let d = 1; d <= lastDay.getDate(); d++)
    cells.push({ date: new Date(year, month, d), currentMonth: true });
  const remaining = (7 - (cells.length % 7)) % 7;
  for (let i = 1; i <= remaining; i++)
    cells.push({ date: new Date(year, month + 1, i), currentMonth: false });
  return cells;
}

// Verifica se un evento copre una certa data
function eventoCoversDate(ev, dateStr) {
  const start = ev.data_inizio;
  const end   = ev.data_fine || ev.data_inizio;
  return dateStr >= start && dateStr <= end;
}

const INP = {
  width: '100%', boxSizing: 'border-box', borderRadius: 'var(--radius-base)',
  border: '1px solid var(--ds-border)', padding: '8px 10px',
  fontSize: 12, outline: 'none',
  background: 'var(--ds-bg)', color: 'var(--ds-text)',
};

// Formato date/ore per la vista dettaglio (gg/mm/aaaa, HH:MM).
function fmtDataUI(s) { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; }
function fmtOraUI(t) { return t ? String(t).slice(0, 5) : ''; }
function fmtIntervalloData(ev) {
  const a = fmtDataUI(ev.data_inizio);
  return (ev.data_fine && ev.data_fine !== ev.data_inizio) ? `${a} → ${fmtDataUI(ev.data_fine)}` : a;
}
function fmtIntervalloOra(ev) {
  const a = fmtOraUI(ev.ora_inizio), b = fmtOraUI(ev.ora_fine);
  return (a && b) ? `${a} – ${b}` : (a || b);
}

// Riga label/valore del dettaglio.
function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: 'var(--ds-muted)' }}>{label}</span>
      <span style={{ color: 'var(--ds-text)', fontWeight: 500, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ── Micro-vista dettaglio evento (FE-052) ────────────────────────────────────
// Mostra le note INTERE come testo (pre-wrap), niente parsing del 📍
// (il tag è un formato di scrittura dell'handler, non un contratto): il 📍 è già
// leggibile da solo e le note possono contenere anche testo dell'utente. Nessuna
// sezione "Note" se le note sono vuote. Componente unico, riusato dal click sul
// chip e dalla lista eventi del giorno (voce 18).
function EventoDettaglio({ evento, onClose, onChanged }) {
  const { user } = useAuth();
  // ARC-017: `destroy` sul ViewSet e' ristretto a IsTenantOwner. Il bottone non
  // c'e' per chi non e' titolare — altrimenti offriremmo un'azione che il
  // backend rifiutera' con un 403, che a schermo diventa un guasto.
  const isOwner = user?.ruolo === 'titolare';

  const [modo, setModo] = useState(null);          // null | 'sposta' | 'conferma-elimina'
  const [nuovaData, setNuovaData] = useState(evento.data_inizio || '');
  const [inCorso, setInCorso] = useState(false);
  const [erroreAzione, setErroreAzione] = useState('');

  // Spostare un evento con una durata significa spostare TUTTO l'intervallo:
  // se cambiassimo solo l'inizio, un evento di tre giorni ne durerebbe uno o
  // cinque a seconda del verso. La fine trasla dello stesso numero di giorni.
  const salvaSpostamento = async () => {
    if (!nuovaData || nuovaData === evento.data_inizio) { setModo(null); return; }
    setInCorso(true); setErroreAzione('');
    try {
      const payload = { data_inizio: nuovaData };
      if (evento.data_fine) {
        const giorni = Math.round(
          (new Date(nuovaData) - new Date(evento.data_inizio)) / 86400000
        );
        const fine = new Date(evento.data_fine);
        fine.setDate(fine.getDate() + giorni);
        payload.data_fine = `${fine.getFullYear()}-${String(fine.getMonth() + 1).padStart(2, '0')}-${String(fine.getDate()).padStart(2, '0')}`;
      }
      await api.patch(`/eventi/${evento.id}/`, payload);
      onChanged?.();
      onClose();
    } catch {
      setErroreAzione('Non sono riuscito a spostare l’evento. Riprova.');
    } finally {
      setInCorso(false);
    }
  };

  const eliminaEvento = async () => {
    setInCorso(true); setErroreAzione('');
    try {
      await api.delete(`/eventi/${evento.id}/`);
      onChanged?.();
      onClose();
    } catch (err) {
      setErroreAzione(
        err?.response?.status === 403
          ? 'Solo il titolare può eliminare un evento.'
          : 'Non sono riuscito a eliminare l’evento. Riprova.'
      );
    } finally {
      setInCorso(false);
    }
  };

  const meta = TIPO_META[evento.tipo] || TIPO_META.altro;
  const evColor = evento.colore || meta.color;
  const note = (evento.note || '').trim();
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 44 }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 420px)', maxHeight: '85vh', overflowY: 'auto', zIndex: 45,
        background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'var(--font-display)', color: 'var(--ink)', lineHeight: 1.25 }}>
            {evento.titolo || meta.label}
          </span>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', padding: 0, lineHeight: 1, flexShrink: 0 }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        <span style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 500, padding: '2px 10px', borderRadius: 99, background: tint(evColor, 14), color: evColor }}>
          {meta.label}
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <DetailRow label="Data" value={fmtIntervalloData(evento)} />
          {(evento.ora_inizio || evento.ora_fine) && <DetailRow label="Ora" value={fmtIntervalloOra(evento)} />}
          {evento.cantiere_titolo && <DetailRow label="Cantiere" value={evento.cantiere_titolo} />}
        </div>
        {note && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 }}>Note</div>
            <div style={{ fontSize: 13, color: 'var(--ds-text)', whiteSpace: 'pre-wrap', lineHeight: 1.5, background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: '10px 12px' }}>
              {note}
            </div>
          </div>
        )}

        {erroreAzione && (
          <div style={{ fontSize: 12, color: 'var(--danger, var(--ink))', background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: '8px 10px' }}>
            {erroreAzione}
          </div>
        )}

        {modo === 'sposta' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--ds-border)', paddingTop: 12 }}>
            <label htmlFor="ev-nuova-data" style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)' }}>
              Nuova data{evento.data_fine ? ' di inizio (la fine trasla di altrettanto)' : ''}
            </label>
            <input
              id="ev-nuova-data" type="date" value={nuovaData}
              onChange={(e) => setNuovaData(e.target.value)}
              style={{ fontSize: 13, padding: '8px 10px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ink)' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={salvaSpostamento} disabled={inCorso}
                style={{ flex: 1, padding: '8px 14px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: 'var(--ink)', fontSize: 13, fontWeight: 500, cursor: inCorso ? 'default' : 'pointer', opacity: inCorso ? 0.6 : 1 }}>
                {inCorso ? 'Sposto...' : 'Sposta'}
              </button>
              <button onClick={() => { setModo(null); setNuovaData(evento.data_inizio || ''); setErroreAzione(''); }} disabled={inCorso}
                style={{ padding: '8px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-muted)', fontSize: 13, cursor: 'pointer' }}>
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Azione distruttiva -> conferma esplicita (regola 14), e la conferma
            dice COSA sparisce, non "sei sicuro?". */}
        {modo === 'conferma-elimina' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px solid var(--ds-border)', paddingTop: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>
              Elimino <strong>{evento.titolo || meta.label}</strong> del {fmtIntervalloData(evento)}? Non si pu&ograve; annullare.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={eliminaEvento} disabled={inCorso}
                style={{ flex: 1, padding: '8px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--danger, var(--ink))', fontSize: 13, fontWeight: 500, cursor: inCorso ? 'default' : 'pointer', opacity: inCorso ? 0.6 : 1 }}>
                {inCorso ? 'Elimino...' : 'Sì, elimina'}
              </button>
              <button onClick={() => { setModo(null); setErroreAzione(''); }} disabled={inCorso}
                style={{ padding: '8px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-muted)', fontSize: 13, cursor: 'pointer' }}>
                Annulla
              </button>
            </div>
          </div>
        )}

        {modo === null && (
          <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--ds-border)', paddingTop: 12 }}>
            <button onClick={() => setModo('sposta')}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ink)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              <CalendarClock size={15} strokeWidth={1.8} /> Sposta
            </button>
            {isOwner && (
              <button onClick={() => setModo('conferma-elimina')} aria-label="Elimina evento"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-muted)', fontSize: 13, cursor: 'pointer' }}>
                <Trash2 size={15} strokeWidth={1.8} /> Elimina
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Lista eventi del giorno (voce 18) ────────────────────────────────────────
// Aperta dal "+N altri": dà una destinazione agli eventi nascosti oltre i primi 3
// (prima era un vicolo cieco). Ogni riga apre la stessa micro-vista dettaglio.
function GiornoEventi({ dateStr, eventi, onSelect, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 42 }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 420px)', maxHeight: '85vh', overflowY: 'auto', zIndex: 43,
        background: 'var(--ds-bg)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Eventi del {fmtDataUI(dateStr)}</span>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', padding: 0, lineHeight: 1 }}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {eventi.map(ev => {
            const meta = TIPO_META[ev.tipo] || TIPO_META.altro;
            const evColor = ev.colore || meta.color;
            return (
              <button key={ev.id} onClick={() => onSelect(ev)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', width: '100%', textAlign: 'left',
                borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer',
              }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: evColor, flexShrink: 0 }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ds-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.titolo || meta.label}</span>
                {ev.ora_inizio && <span style={{ fontSize: 11, color: 'var(--ds-muted)', flexShrink: 0 }}>{fmtOraUI(ev.ora_inizio)}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── Componente principale ────────────────────────────────────────────────────

export default function Calendario() {
  const today    = new Date();
  const todayKey = dateKey(today);

  const [tab, setTab]               = useState('mensile'); // 'mensile' | 'pianificazione'
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [eventi, setEventi]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [apiError, setApiError]     = useState(false);
  const [cantieriList, setCantieriList] = useState([]);
  const [showPanel, setShowPanel]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [toast, setToast]           = useState('');
  // Viste di lettura (FE-052 + voce 18): dettaglio evento + elenco eventi del giorno.
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [dayList, setDayList]       = useState(null); // { dateStr, eventi }

  const [form, setForm] = useState({
    titolo: '', tipo: 'sopralluogo',
    data_inizio: todayKey, data_fine: '',
    ora_inizio: '', ora_fine: '',
    cantiere: '', note: '',
  });

  // ── Data loading ────────────────────────────────────────────────────────

  const reloadEventi = () => {
    api.get('/eventi/', { params: { ordering: 'data_inizio', page_size: EVENTI_PAGE_SIZE } })
      .then(r => setEventi(r.data?.results || r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    api.get('/eventi/', { params: { ordering: 'data_inizio', page_size: EVENTI_PAGE_SIZE } })
      .then(r => setEventi(r.data?.results || r.data || []))
      .catch(() => { setApiError(true); setEventi([]); })
      .finally(() => setLoading(false));

    cantieri.list({ page_size: 100 })
      .then(r => setCantieriList(r.data?.results || r.data || []))
      .catch(() => {});
  }, []);

  const showToast = (msg) => { setToast(msg); };

  // Cleanup timeout per toast (#48 — previene memory leak)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ESC chiude le viste di lettura (prima il dettaglio, poi la lista giorno).
  useEffect(() => {
    if (!selectedEvento && !dayList) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedEvento) setSelectedEvento(null);
      else setDayList(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedEvento, dayList]);

  // ── Panel ────────────────────────────────────────────────────────────────

  const openPanel = (date) => {
    const key = dateKey(date);
    setForm({ titolo: '', tipo: 'sopralluogo', data_inizio: key, data_fine: '', ora_inizio: '', ora_fine: '', cantiere: '', note: '' });
    setSaveError('');
    setShowPanel(true);
  };

  // Apri panel creazione automaticamente se navigazione da Home/sidebar (+Nuovo evento)
  const location = useLocation();
  useEffect(() => {
    if (location.pathname === '/calendario/nuovo') openPanel(new Date());
  }, []); // eslint-disable-line

  const handleSave = async () => {
    if (!form.titolo.trim()) { setSaveError('Il titolo è obbligatorio.'); return; }
    if (form.tipo === 'esecuzione_lavori' && !form.data_fine) { setSaveError('Per "Esecuzione lavori" inserisci la data di fine.'); return; }
    setSaving(true); setSaveError('');
    try {
      const payload = {
        titolo:      form.titolo,
        tipo:        form.tipo,
        data_inizio: form.data_inizio,
        ...(form.data_fine   && { data_fine:   form.data_fine }),
        ...(form.ora_inizio  && { ora_inizio:  form.ora_inizio }),
        ...(form.ora_fine    && { ora_fine:    form.ora_fine }),
        ...(form.cantiere    && { cantiere:    form.cantiere }),
        ...(form.note        && { note:        form.note }),
      };
      await api.post('/eventi/', payload);
      reloadEventi();
      setShowPanel(false);
      showToast('Evento salvato ✓');
    } catch (e) {
      const d = e?.response?.data;
      setSaveError(d?.detail || (typeof d === 'object' ? JSON.stringify(d) : null) || 'Errore nel salvataggio.');
    }
    setSaving(false);
  };

  // ── Calendar data ────────────────────────────────────────────────────────

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const cells = getMonthCells(year, month);

  // Per ogni cella, calcola gli eventi che la coprono
  const eventiByDay = {};
  for (const cell of cells) {
    const key = dateKey(cell.date);
    eventiByDay[key] = eventi.filter(ev => eventoCoversDate(ev, key));
  }

  // ── Gantt data ───────────────────────────────────────────────────────────
  // Finestra configurabile dall'utente (default: mese corrente)
  const [ganttMese, setGanttMese] = useState(currentDate.getMonth());
  const [ganttAnno, setGanttAnno] = useState(currentDate.getFullYear());

  const ganttStart = new Date(ganttAnno, ganttMese, 1);
  const ganttEndDate = new Date(ganttAnno, ganttMese + 1, 0); // ultimo giorno del mese
  const GANTT_DAYS = ganttEndDate.getDate();
  const ganttDays  = Array.from({ length: GANTT_DAYS }, (_, i) => addDays(ganttStart, i));
  const ganttEnd   = ganttDays[GANTT_DAYS - 1];

  // Include esecuzione_lavori + fase_cantiere nel Gantt
  const TIPI_PIANIFICAZIONE = ['esecuzione_lavori', 'fase_cantiere'];
  const esecuzioneEventi = eventi.filter(ev => {
    if (!TIPI_PIANIFICAZIONE.includes(ev.tipo)) return false;
    const start = ev.data_inizio;
    const end   = ev.data_fine || ev.data_inizio;
    return end >= dateKey(ganttStart) && start <= dateKey(ganttEnd);
  });

  // Cantieri con eventi nella finestra Gantt
  const cantieriGantt = [];
  const seen = new Set();
  for (const ev of esecuzioneEventi) {
    const cid = ev.cantiere || '__nessuno__';
    if (!seen.has(cid)) {
      seen.add(cid);
      cantieriGantt.push({ id: cid, titolo: ev.cantiere_titolo || 'Senza cantiere', eventi: [] });
    }
    cantieriGantt.find(c => c.id === cid)?.eventi.push(ev);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ padding: '28px 20px 28px 32px' }}>
        <div style={{ height: 28, background: 'var(--ds-border)', borderRadius: 6, width: 160, marginBottom: 12 }} className="skeleton" />
        <div style={{ height: 14, background: 'var(--ds-border)', borderRadius: 4, width: 280, marginBottom: 24 }} className="skeleton" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
          {Array.from({ length: 35 }, (_, i) => (
            <div key={i} style={{ height: 80, background: 'var(--ds-border)', borderRadius: 8 }} className="skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 100, background: 'var(--brand)', color: '#fff', borderRadius: 'var(--radius-base)', padding: '10px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(46,160,132,0.35)' }}>
          {toast}
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, fontFamily: 'var(--font-display)', color: 'var(--ink)', margin: 0, lineHeight: 1.2 }}>Calendario</h1>
          <p style={{ fontSize: 12, color: 'var(--ds-muted)', margin: '5px 0 0' }}>
            Pianifica attività e monitora l'esecuzione dei cantieri
          </p>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--chip)', borderRadius: 'var(--radius-base)', padding: 4, border: '1px solid var(--ds-border)' }}>
          {[{ key: 'mensile', label: 'Mensile' }, { key: 'pianificazione', label: 'Pianificazione' }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '7px 16px', borderRadius: 'var(--radius-base)', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                background: tab === t.key ? 'var(--ds-bg)' : 'transparent',
                color: tab === t.key ? 'var(--brand)' : 'var(--ds-muted)',
                boxShadow: tab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* API error banner */}
      {apiError && (
        <div style={{ background: tint('var(--warn)', 12), border: '1px solid var(--warn)', borderRadius: 'var(--radius-base)', padding: '10px 16px', fontSize: 12, color: 'var(--ds-text)', marginBottom: 16 }}>
          <AlertTriangle size={14} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Endpoint /api/eventi/ non raggiungibile. Verifica che la migrazione sia stata eseguita su Railway.
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          TAB: MENSILE
      ════════════════════════════════════════════════════════════════ */}
      {tab === 'mensile' && (
        <>
          {/* Navigazione mese */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
              style={{ width: 32, height: 32, borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChevronLeft size={14} strokeWidth={2.5} />
            </button>
            <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', minWidth: 160, textAlign: 'center' }}>
              {MESI[month]} {year}
            </span>
            <button onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
              style={{ width: 32, height: 32, borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChevronRight size={14} strokeWidth={2.5} />
            </button>
            <button onClick={() => setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1))}
              style={{ padding: '6px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', fontSize: 12, fontWeight: 500, color: 'var(--ds-text)', cursor: 'pointer' }}>
              Oggi
            </button>
          </div>

          {/* Legenda */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {Object.entries(TIPO_META).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: v.color }} />
                <span style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{v.label}</span>
              </div>
            ))}
          </div>

          {/* Griglia */}
          <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--ds-border)' }}>
              {GIORNI.map(g => (
                <div key={g} style={{ padding: '10px 0', textAlign: 'center', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{g}</div>
              ))}
            </div>

            {/* ④ — minmax(0,1fr) invece di 1fr: `1fr` = minmax(auto,1fr) e il
                minimo `auto` cresce fino al chip nowrap più largo (titolo lungo)
                → colonne sbilanciate e ellipsis del chip che non scatta. minmax(0,…)
                blocca la traccia a prescindere dal contenuto: l'ellipsis già presente
                sul chip finalmente tronca con "…". */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {cells.map((cell, idx) => {
                const key      = dateKey(cell.date);
                const isToday  = key === todayKey;
                const dayEventi = eventiByDay[key] || [];
                const visible  = dayEventi.slice(0, 3);
                const extra    = dayEventi.length - 3;
                const isLastRow = idx >= cells.length - 7;

                return (
                  <div key={key + idx} onClick={() => openPanel(cell.date)}
                    style={{
                      minHeight: 90, padding: '6px 8px',
                      borderRight: (idx + 1) % 7 === 0 ? 'none' : '1px solid var(--ds-border)',
                      borderBottom: isLastRow ? 'none' : '1px solid var(--ds-border)',
                      cursor: 'pointer', opacity: cell.currentMonth ? 1 : 0.3,
                      background: isToday ? tint('var(--brand)', 6) : 'transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => { if (!isToday) e.currentTarget.style.background = tint('var(--brand)', 3); }}
                    onMouseLeave={e => { e.currentTarget.style.background = isToday ? tint('var(--brand)', 6) : 'transparent'; }}
                  >
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', fontSize: 12, fontWeight: 500, color: isToday ? '#fff' : 'var(--ds-text)', background: isToday ? 'var(--brand)' : 'transparent', marginBottom: 4 }}>
                      {cell.date.getDate()}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {visible.map((ev, i) => {
                        const meta    = TIPO_META[ev.tipo] || TIPO_META.altro;
                        const evColor = ev.colore || meta.color;
                        const isFirst = key === ev.data_inizio;
                        const isLast  = key === (ev.data_fine || ev.data_inizio);
                        const multi   = ev.data_fine && ev.data_fine !== ev.data_inizio;
                        return (
                          <div key={i}
                            onClick={(e) => { e.stopPropagation(); setSelectedEvento(ev); }}
                            title={ev.titolo || meta.label}
                            style={{
                            fontSize: 11, fontWeight: 500, color: '#fff',
                            background: evColor,
                            borderRadius: multi ? (isFirst ? '4px 0 0 4px' : isLast ? '0 4px 4px 0' : '0') : 4,
                            padding: '1px 5px',
                            minHeight: 16,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            opacity: multi && !isFirst ? 0.85 : 1,
                          }}>
                            {isFirst || !multi ? (ev.titolo || ev.tipo) : '\u00A0'}
                          </div>
                        );
                      })}
                      {extra > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDayList({ dateStr: key, eventi: dayEventi }); }}
                          style={{
                            fontSize: 11, color: 'var(--ds-muted)', fontWeight: 500, cursor: 'pointer',
                            background: 'none', border: 'none', padding: '1px 2px', textAlign: 'left', width: 'fit-content',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--brand)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ds-muted)'; }}
                        >+{extra} altri</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
          TAB: PIANIFICAZIONE (Gantt)
      ════════════════════════════════════════════════════════════════ */}
      {tab === 'pianificazione' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            {/* Navigazione mese */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => { if (ganttMese === 0) { setGanttMese(11); setGanttAnno(ganttAnno - 1); } else setGanttMese(ganttMese - 1); }}
                style={{ width: 32, height: 32, borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={14} strokeWidth={2.5} /></button>
              <span style={{ fontSize: 14, fontWeight: 500, minWidth: 130, textAlign: 'center' }}>
                {MESI[ganttMese]} {ganttAnno}
              </span>
              <button onClick={() => { if (ganttMese === 11) { setGanttMese(0); setGanttAnno(ganttAnno + 1); } else setGanttMese(ganttMese + 1); }}
                style={{ width: 32, height: 32, borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={14} strokeWidth={2.5} /></button>
              <button onClick={() => { setGanttMese(new Date().getMonth()); setGanttAnno(new Date().getFullYear()); }}
                style={{ padding: '4px 10px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 11, color: 'var(--ds-muted)' }}>Oggi</button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--ds-muted)', alignSelf: 'center' }}>Fasi cantiere + Esecuzioni lavori</span>
              <button onClick={() => openPanel(today)}
                style={{ padding: '8px 16px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                + Nuova esecuzione lavori
              </button>
            </div>
          </div>

          {cantieriGantt.length === 0 ? (
            <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', padding: '60px 0', textAlign: 'center' }}>
              <div style={{ marginBottom: 12 }}><Calendar size={40} strokeWidth={1.5} color="var(--ds-muted)" /></div>
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ds-text)', marginBottom: 8 }}>Nessun lavoro pianificato</div>
              <div style={{ fontSize: 13, color: 'var(--ds-muted)', marginBottom: 24 }}>Nessuna fase cantiere o esecuzione lavori in {MESI[ganttMese]} {ganttAnno}</div>
              <button onClick={() => { openPanel(today); setForm(f => ({ ...f, tipo: 'esecuzione_lavori' })); }}
                style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                + Pianifica lavori
              </button>
            </div>
          ) : (
            <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', overflow: 'auto' }}>
              {/* Header date */}
              <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${GANTT_DAYS}, minmax(32px, 1fr))`, borderBottom: '1px solid var(--ds-border)' }}>
                <div style={{ padding: '10px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', background: 'var(--chip)' }}>
                  Cantiere
                </div>
                {ganttDays.map((d, i) => {
                  const isT = dateKey(d) === todayKey;
                  const isMonday = d.getDay() === 1;
                  return (
                    <div key={i} style={{
                      padding: '6px 2px', textAlign: 'center', fontSize: 11, fontWeight: 500,
                      color: isT ? 'var(--brand)' : 'var(--ds-muted)',
                      background: isT ? tint('var(--brand)', 8) : i % 2 === 0 ? 'var(--chip)' : 'var(--ds-bg)',
                      borderLeft: isMonday ? '1px solid var(--ds-border)' : 'none',
                    }}>
                      <div>{d.getDate()}</div>
                      <div style={{ fontSize: 9, opacity: 0.7 }}>{GIORNI[(d.getDay() + 6) % 7]}</div>
                    </div>
                  );
                })}
              </div>

              {/* Righe cantieri */}
              {cantieriGantt.map((cantiere, ci) => (
                <div key={cantiere.id} style={{ display: 'grid', gridTemplateColumns: `180px repeat(${GANTT_DAYS}, minmax(32px, 1fr))`, borderBottom: ci < cantieriGantt.length - 1 ? '1px solid var(--ds-border)' : 'none', minHeight: 48 }}>
                  {/* Nome cantiere */}
                  <div style={{ padding: '12px 16px', fontSize: 12, fontWeight: 500, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', background: 'var(--chip)', borderRight: '1px solid var(--ds-border)' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 148 }}>
                      {cantiere.titolo}
                    </div>
                  </div>

                  {/* Celle giorno */}
                  {ganttDays.map((d, di) => {
                    const dKey     = dateKey(d);
                    const isT      = dKey === todayKey;
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    const isMonday  = d.getDay() === 1;

                    // Trova eventi che coprono questo giorno per questo cantiere
                    const cellEventi = cantiere.eventi.filter(ev => eventoCoversDate(ev, dKey));

                    return (
                      <div key={di} style={{
                        position: 'relative',
                        background: isT ? tint('var(--brand)', 6) : isWeekend ? tint('var(--ds-muted)', 6) : 'transparent',
                        borderLeft: isMonday ? '1px solid var(--ds-border)' : 'none',
                        display: 'flex', alignItems: 'center', padding: '4px 1px',
                      }}>
                        {cellEventi.map((ev, ei) => {
                          const isStart = dKey === ev.data_inizio;
                          const isEnd   = dKey === (ev.data_fine || ev.data_inizio);
                          const meta    = TIPO_META[ev.tipo] || TIPO_META.altro;
                          return (
                            <div key={ei} title={`${ev.titolo} (${ev.data_inizio} → ${ev.data_fine || ev.data_inizio})`}
                              style={{
                                position: 'absolute', left: 1, right: 1, height: 22,
                                background: meta.color,
                                borderRadius: isStart && isEnd ? 4 : isStart ? '4px 0 0 4px' : isEnd ? '0 4px 4px 0' : 0,
                                opacity: 0.9,
                                display: 'flex', alignItems: 'center', overflow: 'hidden',
                              }}>
                              <span style={{ fontSize: 11, fontWeight: 500, color: '#fff', paddingLeft: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {ev.titolo}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* Tutti gli altri eventi nella finestra (non esecuzione_lavori) */}
          {(() => {
            const ganttStartKey = dateKey(ganttStart);
            const ganttEndKey   = dateKey(ganttEnd);
            const altriEventi   = eventi.filter(ev =>
              !TIPI_PIANIFICAZIONE.includes(ev.tipo) &&
              ev.data_inizio >= ganttStartKey &&
              ev.data_inizio <= ganttEndKey
            );
            if (altriEventi.length === 0) return null;
            return (
              <div style={{ marginTop: 20, background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: '0 6px 18px rgba(35,38,47,0.08)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ds-border)', fontSize: 12, fontWeight: 500, color: 'var(--ds-text)' }}>
                  Altri eventi nel periodo
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {altriEventi.map(ev => {
                    const meta = TIPO_META[ev.tipo] || TIPO_META.altro;
                    return (
                      <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--ds-border)' }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: meta.color, flexShrink: 0 }} />
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ds-text)', flex: 1 }}>{ev.titolo}</div>
                        <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{ev.data_inizio}</div>
                        <div style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: tint(meta.color, 14), color: meta.color, fontWeight: 500 }}>{meta.label}</div>
                        {ev.cantiere_titolo && <div style={{ fontSize: 11, color: 'var(--ds-muted)' }}>{ev.cantiere_titolo}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Side panel ── */}
      {showPanel && (
        <>
          <div onClick={() => setShowPanel(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 40 }} />
          <div style={{ position: 'fixed', right: 0, top: 0, height: '100vh', width: 320, background: 'var(--ds-bg)', borderLeft: '1px solid var(--ds-border)', boxShadow: '-4px 0 20px rgba(35,38,47,0.08)', padding: 20, zIndex: 41, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>Nuovo Evento</span>
              <button onClick={() => setShowPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-muted)', lineHeight: 1, padding: 0 }}><X size={20} strokeWidth={2} /></button>
            </div>

            {/* Titolo */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Titolo *</label>
              <input type="text" placeholder="Es. Posa piastrelle bagno" value={form.titolo}
                onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
                style={INP} autoFocus />
            </div>

            {/* Tipo */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Tipo</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={INP}>
                {TIPI_UTENTE.map(t => <option key={t} value={t}>{TIPO_META[t].label}</option>)}
              </select>
            </div>

            {/* Date — layout dipende dal tipo */}
            {form.tipo === 'esecuzione_lavori' ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Data inizio *</label>
                  <input type="date" value={form.data_inizio}
                    onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} style={INP} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Data fine *</label>
                  <input type="date" value={form.data_fine} min={form.data_inizio}
                    onChange={e => setForm(f => ({ ...f, data_fine: e.target.value }))} style={INP} />
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Data</label>
                  <input type="date" value={form.data_inizio}
                    onChange={e => setForm(f => ({ ...f, data_inizio: e.target.value }))} style={INP} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Ora inizio</label>
                    <input type="time" value={form.ora_inizio} onChange={e => setForm(f => ({ ...f, ora_inizio: e.target.value }))} style={INP} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Ora fine</label>
                    <input type="time" value={form.ora_fine} onChange={e => setForm(f => ({ ...f, ora_fine: e.target.value }))} style={INP} />
                  </div>
                </div>
              </>
            )}

            {/* Info banner per esecuzione_lavori */}
            {form.tipo === 'esecuzione_lavori' && (
              <div style={{ background: tint('var(--brand)', 8), border: '1px solid ' + tint('var(--brand)', 20), borderRadius: 'var(--radius-base)', padding: '8px 10px', fontSize: 11, color: 'var(--brand)' }}>
                L'intervallo apparirà nel Gantt della scheda "Pianificazione"
              </div>
            )}

            {/* Cantiere */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Cantiere</label>
              <select value={form.cantiere} onChange={e => setForm(f => ({ ...f, cantiere: e.target.value }))} style={INP}>
                <option value="">— Nessun cantiere —</option>
                {cantieriList.map(c => (
                  <option key={c.id} value={c.id}>{c.titolo}{c.cliente_nome ? ` — ${c.cliente_nome}` : ''}</option>
                ))}
              </select>
            </div>

            {/* Note */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', display: 'block', marginBottom: 4 }}>Note</label>
              <textarea rows={3} placeholder="Note opzionali…" value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                style={{ ...INP, resize: 'vertical' }} />
            </div>

            {saveError && (
              <div style={{ fontSize: 11, color: 'var(--ds-danger)', background: tint('var(--ds-danger)', 10), borderRadius: 'var(--radius-base)', padding: '8px 10px' }}>{saveError}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', fontSize: 13, fontWeight: 500, padding: '10px 0', cursor: 'pointer' }}>
                {saving ? '…' : 'Salva evento'}
              </button>
              <button onClick={() => setShowPanel(false)}
                style={{ flex: 1, borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'none', color: 'var(--ds-muted)', fontSize: 13, padding: '10px 0', cursor: 'pointer' }}>
                Annulla
              </button>
            </div>
          </div>
        </>
      )}

      {/* Elenco eventi del giorno (voce 18) — dà una destinazione al "+N altri" */}
      {dayList && (
        <GiornoEventi
          dateStr={dayList.dateStr}
          eventi={dayList.eventi}
          onSelect={(ev) => setSelectedEvento(ev)}
          onClose={() => setDayList(null)}
        />
      )}

      {/* Micro-vista dettaglio evento (FE-052) — z-index maggiore, sopra la lista */}
      {selectedEvento && (
        <EventoDettaglio evento={selectedEvento} onClose={() => setSelectedEvento(null)} onChanged={reloadEventi} />
      )}
    </div>
  );
}
